import './Linkify.sass';

import React, { useMemo, ReactNode, FC } from 'react';
import linkifyIt from 'linkify-it';
import { Link } from 'react-router5';

import { FEED, RESOLVE } from '$shared/constants/pages';
import { isTrustedDomain, classNames as cn, formatApiUrl } from '$utils';
import { LinkifyProps } from '$shared/components/Linkify/Linkify.interface';

const inst = linkifyIt().tlds('place', true);

inst.add('@', {
  validate: /^(id|group)\d+|^[a-zA-Z_\d]{4,15}/,
  normalize(match) {
    match.schema = 'mention';
    match.url = match.text.replace(/^@/, '');
  },
});

// inst.add('#', {
//   validate: /^[a-zа-я0-9]+/i,
//   normalize(match) {
//     match.schema = 'hashtag';
//     match.url = '/feed?section=search&query=' + match.text;
//   },
// });

export function getLinks(text: string): string[] {
  const matches = inst.match(text);
  return matches?.map((match) => match.url) || [];
}

export function getLinksRaw(text: string) {
  return inst.match(text);
}

export function findLinkQuill(html: string, opts: any = {}): any[] | null {
  const matches = inst.match(html);
  if (!matches) {
    return null;
  }

  let result: any[] = [];
  let lastIndex = 0;
  for (let match of matches) {
    result.push({
      type: 'text',
      text: html.substring(lastIndex, match.index),
    });

    const trusted = isTrustedDomain(match.url);
    const linkTemplate = {
      type: 'link',
      text: match.text,
      url: match.url,
      target: '_self',
    };
    if (match.schema === 'mention') {
      result.push({
        ...linkTemplate,
        schema: 'mention',
      });
    } else if (match.schema === 'hashtag') {
      result.push({
        ...linkTemplate,
        url: `/feed?section=search&query=${encodeURIComponent(match.text)}`,
        schema: 'hashtag',
      });
    } else if (trusted) {
      result.push(linkTemplate);
    } else {
      result.push({
        ...linkTemplate,
        url: formatApiUrl('/away', {
          url: match.url,
          adId: opts.adId || 0,
        }),
        target: '_blank',
      });
    }
    lastIndex = match.lastIndex;
  }
  result.push({
    type: 'text',
    text: html.substring(lastIndex),
  });

  return result;
}

export function removeLinks(text: string): string {
  if (!text) {
    return '';
  }

  const matches = inst.match(text);
  if (!matches) {
    return text;
  }

  let lastIndex = 0;
  let result: string[] = [];
  for (let match of matches) {
    result.push(text.substring(lastIndex, match.index));
    lastIndex = match.lastIndex;
    //result.push('[Link removed]');
  }

  return result.join('');
}

export function findLink(children: ReactNode, opts: any = {}): ReactNode {
  return React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        children: findLink(child.props.children),
      });
    }

    if (!child || typeof child !== 'string') {
      return null;
    }

    const handleClick = (e) => e.stopPropagation();
    const matches = inst.match(child);
    if (!matches) {
      return child;
    }

    let result: ReactNode[] = [];
    let lastIndex = 0;
    let i = 0;
    for (let match of matches) {
      result.push(child.substring(lastIndex, match.index));

      const trusted = isTrustedDomain(match.url);
      if (match.schema === 'mention') {
        result.push(
          <Link routeName={RESOLVE} routeParams={{ domain: match.url }}>
            {match.text}
          </Link>,
        );
      } else if (match.schema === 'hashtag') {
        result.push(
          <Link
            routeName={FEED}
            routeParams={{
              section: 'search',
              query: match.text,
            }}
          >
            {match.text}
          </Link>,
        );
      } else if (trusted) {
        result.push(
          <a
            href={match.url}
            target="_self"
            key={i + '_' + match.url}
            onClick={handleClick}
            rel="noreferrer"
          >
            {match.text}
          </a>,
        );
      } else {
        result.push(
          <a
            href={formatApiUrl('/away', {
              url: match.url,
              adId: opts.adId || 0,
            })}
            target="_blank"
            key={i + '_' + match.url}
            onClick={handleClick}
            rel="noreferrer"
          >
            {match.text}
          </a>,
        );
      }
      lastIndex = match.lastIndex;
    }
    result.push(child.substring(lastIndex));

    return result;
  });
}

export const Linkify: FC<LinkifyProps> = ({ children, disabled = false, className }) => {
  const prepared = useMemo(() => {
    return disabled ? children : findLink(children);
  }, [disabled, children]);

  return <div className={cn('Linkify', className)}>{prepared}</div>;
};
